import React from "react";
import PanelState from "../MainComponents/PanelState";
import Tally from "../MainComponents/Tally";

/**
 * Task 37, B4 — the dry run: what the sync would do, shown before anything is sent.
 *
 * Presentational. Every decision here was made on the server by the SAME planner the real sync
 * uses, which is the whole point — a preview worked out separately could describe one thing while
 * the sync did another, and a reseller is being asked to trust this before letting billing reach
 * their tenant.
 *
 * Blocked lines are shown ALONGSIDE the rest rather than replacing them. One customer with a
 * deleted agreement must not blank the whole run, and "identify errors" is served by naming each
 * one next to the customer it belongs to.
 *
 * Queueing for syncing is NOT here: the client asked for it on the Sync tab, beside the lines it
 * sends. This tab is where the figures are checked, and it says where to go once they have been.
 */
export default function DryRunPanel({
    summary, preparing, onPrepare, loading, error, onRetry,
    // A master administrator working on another reseller sees that reseller's latest preview but
    // cannot prepare one: preparing reads their ConnectWise and is theirs to start. The server
    // refuses it; here nothing offers it.
    readOnly,
}) {
    const prepared = summary && summary.isPrepared;
    const lines = (summary && summary.lines) || [];
    const busy = preparing || (summary && summary.isPreparing);

    // A FETCH THAT NEVER ARRIVED OUTRANKS A SERVER-REPORTED FAILURE, and is a different offer:
    // "try again" here means fetch the preview again, not prepare a new one. Showing the stale
    // failureReason from the last successful fetch would describe the wrong problem entirely.
    if (!busy && error) {
        return (
            <PanelState
                busy={loading}
                error={error}
                actionLabel="Try again"
                busyLabel="Trying…"
                onAction={onRetry}
            />
        );
    }

    if (!prepared) {
        // Four states share this space, and they are NOT the same message. "Expired" is its own
        // case at the client's request: a reseller who prepared a preview and came back three days
        // later should be told what happened, not shown a screen identical to never having made one.
        // "Failed" is its own case for the same reason, and names the tab that fixes it.
        const expired = !busy && summary && summary.hasExpired;
        const failed = !busy && summary && summary.failureReason;

        // The branches are unchanged; only the markup around them moved into PanelState, which was
        // shaped on this block in the first place. Note `busy` is passed through PanelState's own
        // busy input, so the button disables and relabels exactly as it did.
        //
        // The busy arms are now busyTitle/busyDetail rather than the first branch of title/detail.
        // "Preparing" is not an empty state — this panel is the one place that said so correctly,
        // and it said it by passing a busy-aware title, which is precisely the conflation that let
        // every other panel announce an empty result mid-fetch.
        if (readOnly && !busy) {
            return (
                <PanelState
                    title={failed
                        ? "This reseller's last preview could not be prepared."
                        : expired
                            ? "This reseller's last preview has expired."
                            : "This reseller has not prepared a preview."}
                    detail={failed
                        ? `${summary.failureReason} Only the reseller can prepare a preview.`
                        : "Only the reseller can prepare a preview. Once they have, it shows here."}
                />
            );
        }

        return (
            <PanelState
                busy={busy}
                busyTitle="Preparing your preview…"
                busyDetail="This reads your agreements in ConnectWise. Nothing is changed there."
                title={failed
                    ? "Your preview could not be prepared."
                    : expired
                        ? "Your previous preview has expired."
                        : "No preview has been prepared yet."}
                detail={failed
                    ? summary.failureReason
                    : expired
                        ? "Previews are kept for 72 hours so they cannot be acted on once they are out of date. Prepare a new one to see what would be sent now."
                        : "See exactly what would be sent to ConnectWise, without sending any of it."}
                actionLabel={failed
                    ? "Try again"
                    : expired ? "Prepare a new preview" : "Prepare a preview"}
                busyLabel="Preparing…"
                onAction={onPrepare}
            />
        );
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-start mb-3">
                <div style={{ maxWidth: 700 }}>
                    <p className="text-muted mb-1">
                        What a sync would do right now. Nothing here has been sent to ConnectWise, and
                        nothing will be until you queue it for syncing on the Sync tab.
                    </p>
                    <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                        Prepared {summary.preparedAt ? new Date(summary.preparedAt).toLocaleString() : ""}
                        {summary.preparedBy ? ` by ${summary.preparedBy}` : ""}.
                    </p>
                </div>
                {!readOnly && (
                    <button type="button" className="btn btn-primary" disabled={busy} onClick={onPrepare}>
                        {busy ? "Preparing…" : "Prepare again"}
                    </button>
                )}
            </div>

            {/* Where to go once the figures have been checked — the button lives on the Sync tab.
                Worded for anyone: this tab cannot tell whether a master administrator is acting on
                the reseller's behalf, and only the reseller may queue. The Sync tab says so. */}
            {lines.length > 0 && summary.awaitingApprovalCount > 0 && summary.isCurrent !== false && (
                <div className="alert alert-info py-2" style={{ fontSize: 14 }}>
                    {summary.awaitingApprovalCount} line(s) in this preview are awaiting approval. Once
                    they have been checked, they are queued for syncing on the Sync tab.
                </div>
            )}

            {lines.length > 0 && summary.awaitingApprovalCount === 0 && summary.isCurrent !== false && (
                <div className="alert alert-success py-2" style={{ fontSize: 14 }}>
                    Everything in this preview has been queued for syncing.
                </div>
            )}

            {summary.failureReason && (
                <div className="alert alert-danger py-2" style={{ fontSize: 14 }}>
                    The last attempt to prepare a preview did not finish: {summary.failureReason} What
                    you see below is the previous one.
                </div>
            )}

            {summary.isCurrent === false && (
                <div className="alert alert-warning py-2" style={{ fontSize: 14 }}>
                    Your billing figures have changed since this preview was prepared.{" "}
                    {readOnly
                        ? "The reseller would need to prepare it again to see what would be sent now."
                        : "Prepare it again to see what would be sent now."}
                </div>
            )}

            <div className="d-flex gap-3 mb-3 flex-wrap">
                <Tally label="To create" value={summary.createCount} />
                <Tally label="To update" value={summary.updateCount} />
                <Tally label="To cancel" value={summary.cancelCount} />
                <Tally label="Already correct" value={summary.noChangeCount} />
                <Tally label="Blocked" value={summary.blockedCount} tone={summary.blockedCount > 0 ? "danger" : null} />
            </div>

            {summary.blockedCount > 0 && (
                <div className="alert alert-warning py-2" style={{ fontSize: 14, maxWidth: 820 }}>
                    Some customers cannot be synced yet. Each is listed below with the reason, and
                    the rest are unaffected — a sync would still send everything else.
                </div>
            )}

            {lines.length === 0 ? (
                // N5 — two different empties, said differently. Discarded lines are not shown here,
                // because this is what would be sent and they would not be; but an empty preview must
                // not suggest nothing was calculated when lines exist and were thrown away.
                summary.discardedCount > 0 ? (
                    <PanelState
                        title="There is nothing waiting to be sent."
                        detail={`${summary.discardedCount} calculated line(s) were discarded, so they are not included here. To bring any back, open the Sync tab, tick "Show discarded" and rebuild the ones you need.`}
                    />
                ) : (
                    <PanelState
                        title="There is nothing waiting to be sent."
                        detail="Lines appear here once a month's billing has been calculated."
                    />
                )
            ) : (
                <table className="table table-sm align-middle">
                    <thead>
                        <tr>
                            <th>Customer</th>
                            <th>Type</th>
                            <th>What would happen</th>
                            <th className="text-end">Quantity</th>
                            <th className="text-end">Unit price</th>
                            <th>Effective</th>
                        </tr>
                    </thead>
                    <tbody>
                        {lines.map((line) => (
                            <tr key={line.billingSyncItemId} className={line.blockedReason ? "table-light" : undefined}>
                                <td>{line.customerLabel}</td>
                                <td>{line.additionType === "STORAGE" ? "Storage" : "Licence"}</td>
                                <td>
                                    <ActionLabel action={line.plannedAction} />
                                    {line.blockedReason && (
                                        <div className="text-danger" style={{ fontSize: 12 }}>
                                            {line.blockedReason}
                                        </div>
                                    )}
                                </td>
                                <td className="text-end">{line.quantity}</td>
                                <td className="text-end">{line.unitPrice}</td>
                                <td>
                                    {line.effectiveDate
                                        ? new Date(line.effectiveDate).toLocaleDateString()
                                        : ""}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

/**
 * The planner's own vocabulary, in words a reseller reads rather than the enum names the server
 * stores. Replace is deliberately described by what it does rather than by its name, which would
 * mean nothing to the person reading it.
 */
function ActionLabel({ action }) {
    const words = {
        Create: ["Add a new addition", "text-success"],
        Update: ["Update the existing addition", "text-primary"],
        Replace: ["Replace it — the mapped product changed", "text-primary"],
        Cancel: ["End the addition", "text-warning"],
        NoChange: ["Nothing — already correct", "text-muted"],
        Blocked: ["Cannot proceed", "text-danger"],
    };

    const [label, tone] = words[action] || [action, "text-muted"];
    return <span className={tone}>{label}</span>;
}

